import React, { useEffect, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import ProtectedRoute from './components/common/ProtectedRoute';
import PublicRoute from './components/common/PublicRoute';
import AdminRoute from './components/common/AdminRoute';
import AnalyticsTracker from './components/common/AnalyticsTracker';
import CookieManager from './components/common/CookieManager';
import NetworkStatusBanner from './components/common/NetworkStatusBanner';
import BetaModeBanner from './components/common/BetaModeBanner';
import MaintenanceGuard from './components/common/MaintenanceGuard';
import SeoMetadata from './components/SeoMetadata';
import HomePage from './pages/HomePage';
import NutritionPage from './pages/Nutrition/NutritionPage';

// Pages (lazy-loaded : chaque page part dans son propre chunk)
const LoginPage = lazy(() => import('./pages/Auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/Auth/RegisterPage'));
const ResetPasswordPage = lazy(() => import('./pages/Auth/ResetPasswordPage'));
const FriendsPage = lazy(() => import('./pages/Friends/FriendsPage'));
const WeightPage = lazy(() => import('./pages/Health/WeightPage'));
const SleepPage = lazy(() => import('./pages/Health/SleepPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const UserSettingsPage = lazy(() => import('./pages/User/UserSettingsPage'));
const UserProfilPage = lazy(() => import('./pages/User/UserProfilPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const NewsletterConfirmPage = lazy(() => import('./pages/NewsletterConfirmPage'));
const NewsletterUnsubscribePage = lazy(() => import('./pages/NewsletterUnsubscribePage'));
const Offers = lazy(() => import('./pages/Offers'));
const PremiumCheckout = lazy(() => import('./pages/PremiumCheckout'));
const ProCheckout = lazy(() => import('./pages/ProCheckout'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const NewsletterAdminPage = lazy(() => import('./pages/Admin/NewsletterAdminPage'));
const UsersAdminPage = lazy(() => import('./pages/Admin/UsersAdminPage'));
const AdminDashboardPage = lazy(() => import('./pages/Admin/AdminDashboardPage'));
const AdminStatsPage = lazy(() => import('./pages/Admin/AdminStatsPage'));
const AdminSecurityPage = lazy(() => import('./pages/Admin/AdminSecurityPage'));
const ContactAdminPage = lazy(() => import('./pages/Admin/ContactAdminPage'));
const AdminAuditLogsPage = lazy(() => import('./pages/Admin/AdminAuditLogsPage'));

function App() {
  useEffect(() => {
    if (typeof window === 'undefined' || !document?.body) {
      return undefined;
    }

    const BUTTON_SELECTOR = [
      'button',
      'input[type="button"]',
      'input[type="submit"]',
      'input[type="reset"]',
      '[role="button"]',
      '.btn',
      '[class*="btn"]',
      '[class*="Button"]'
    ].join(', ');

    const setFrozenHoverVars = (element: Element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }
      const computed = window.getComputedStyle(element);
      element.style.setProperty('--np-hover-bg', computed.backgroundColor);
      element.style.setProperty('--np-hover-color', computed.color);
      element.style.setProperty('--np-hover-border', computed.borderColor);
      element.style.setProperty('--np-hover-shadow', computed.boxShadow);
      element.style.setProperty('--np-hover-opacity', computed.opacity);
      element.style.setProperty('--np-hover-filter', computed.filter);
      element.style.setProperty('--np-hover-transform', computed.transform === 'none' ? 'none' : computed.transform);
    };

    const applyHoverFreeze = () => {
      document.querySelectorAll(BUTTON_SELECTOR).forEach(setFrozenHoverVars);
    };

    applyHoverFreeze();

    const observer = new MutationObserver((mutations) => {
      const hasRelevantMutation = mutations.some(
        (mutation) => mutation.type === 'childList' || mutation.type === 'attributes'
      );

      if (!hasRelevantMutation) {
        return;
      }
      window.requestAnimationFrame(applyHoverFreeze);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled']
    });

    window.addEventListener('resize', applyHoverFreeze);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', applyHoverFreeze);
    };
  }, []);

  return (
    <AuthProvider>
      <Router>
        <SeoMetadata />
        <AnalyticsTracker />
        <CookieManager />
        <NetworkStatusBanner />
        <BetaModeBanner />
        <MaintenanceGuard>
         <div className="App">
          <Routes>
            {/* Routes publiques */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/legal" element={<LegalPage />} />

            <Route path="/friends/:username/profile" element={
              <ProtectedRoute>
                <UserProfilPage />
              </ProtectedRoute>
            } />

            {/* Route pour journal public - accessible sans authentification */}
            <Route path="/friends/*" element={
              <ProtectedRoute>
                <FriendsPage />
              </ProtectedRoute>
            } />

            <Route path="/nutrition/*" element={
              <ProtectedRoute>
                <NutritionPage />
              </ProtectedRoute>
            } />

            <Route path="/poids/*" element={
              <ProtectedRoute>
                <WeightPage />
              </ProtectedRoute>
            } />

            <Route path="/sommeil/*" element={
              <ProtectedRoute>
                <SleepPage />
              </ProtectedRoute>
            } />

            <Route path="/users/profile" element={
              <ProtectedRoute>
                <UserProfilPage />
              </ProtectedRoute>
            } />

            <Route path="/users/settings" element={
              <ProtectedRoute>
                <UserSettingsPage />
              </ProtectedRoute>
            } />

            <Route path="/admin" element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            } />

            <Route path="/admin/users" element={
              <AdminRoute>
                <UsersAdminPage />
              </AdminRoute>
            } />

            <Route path="/admin/stats" element={
              <AdminRoute>
                <AdminStatsPage />
              </AdminRoute>
            } />

            <Route path="/admin/security" element={
              <AdminRoute>
                <AdminSecurityPage />
              </AdminRoute>
            } />

            <Route path="/admin/newsletters" element={
              <AdminRoute>
                <NewsletterAdminPage />
              </AdminRoute>
            } />

            <Route path="/admin/contacts" element={
              <AdminRoute>
                <ContactAdminPage />
              </AdminRoute>
            } />

            <Route path="/admin/audit-logs" element={
              <AdminRoute>
                <AdminAuditLogsPage />
              </AdminRoute>
            } />

            {/* Abonnements */}

            <Route path="/offers" element={<Offers />} />
            <Route path="/premium-checkout" element={<PremiumCheckout />} />
            <Route path="/pro-checkout" element={<ProCheckout />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />

            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/newsletter/confirm/:token" element={<NewsletterConfirmPage />} />
            <Route path="/newsletter/unsubscribe/:token" element={<NewsletterUnsubscribePage />} />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
         </div>
        </MaintenanceGuard>
      </Router>
    </AuthProvider>
  );
}

export default App;
